import {
  VaccinationClinicOpeningHoursStoryblok,
  VaccinationClinicTypeStoryblok,
  VaccineCampaignStoryblok,
  VaccineTypeStoryblok
} from "@/types/component-types"
import { ISbStoryData } from "@storyblok/react/rsc"

/**
 * Opening hours and campaigns live in their own stories now, but the components still read them off
 * the clinic/vaccine content they always did. Merging here — once, where the data is fetched —
 * keeps that true: no prop threaded through four layers of client components, and no component that
 * has to know which story a field came from.
 *
 * Why the fields moved at all: `data/oppettider/<webbId>` has exactly one writer, the Öppettider
 * sync, so it can be published on every run. A clinic config cannot, because publishing a story
 * publishes whatever else an editor happens to have in its draft.
 */

/** The fields the öppettider story owns. Anything else on the clinic config is left alone. */
const HOURS_FIELDS = [
  "opening_hours_content",
  "deviating_opening_hours",
  "deviating_start_date",
  "deviating_end_date",
  "extra_opening_hours",
  "extra_start_date",
  "extra_end_date"
] as const

/** The fields the campaign story owns. */
const CAMPAIGN_FIELDS = [
  "campaign_enabled",
  "campaign_start_date",
  "campaign_end_date",
  "campaign_price",
  "campaign_description",
  "campaign_image",
  "override_campaign_image",
  "overrided_campaign_image_background_asset",
  "overrided_campaign_image_title",
  "overrided_campaign_image_subtitle"
] as const

/**
 * The shape the components read after a merge: the story's own fields plus the ones its companion
 * story owns. The companion fields no longer exist on `vaccination_clinic_type` / `vaccine_type`, so
 * without this the merged object would have a type that denies the very fields it carries.
 */
export type ClinicOpeningHoursFields = Pick<VaccinationClinicOpeningHoursStoryblok, (typeof HOURS_FIELDS)[number]>
export type VaccinationClinicWithHours = VaccinationClinicTypeStoryblok & ClinicOpeningHoursFields

export type VaccineCampaignFields = Pick<VaccineCampaignStoryblok, (typeof CAMPAIGN_FIELDS)[number]>
export type VaccineWithCampaign = VaccineTypeStoryblok & VaccineCampaignFields

const pick = <T extends object>(source: T | undefined, keys: readonly string[]) => {
  const out: Record<string, unknown> = {}
  if (!source) return out
  for (const key of keys) {
    const value = (source as Record<string, unknown>)[key]
    if (value !== undefined) out[key] = value
  }
  return out
}

export const mergeClinicHours = (
  clinic: VaccinationClinicTypeStoryblok,
  hoursByWebbId: Record<string, VaccinationClinicOpeningHoursStoryblok>
): VaccinationClinicWithHours => {
  const webbId = String(clinic.sheet_webb_id ?? "").trim()
  const hours = webbId ? hoursByWebbId[webbId] : undefined
  // No story yet for this clinic — the page renders without hours rather than not at all.
  if (!hours) return clinic
  return { ...clinic, ...pick(hours, HOURS_FIELDS) } as VaccinationClinicWithHours
}

export const mergeVaccineCampaign = (
  vaccine: VaccineTypeStoryblok,
  campaign: VaccineCampaignStoryblok | undefined
): VaccineWithCampaign =>
  campaign ? ({ ...vaccine, ...pick(campaign, CAMPAIGN_FIELDS) } as VaccineWithCampaign) : vaccine

type Blok = { component?: string; items?: unknown[] } & Record<string, unknown>

/**
 * The overview sections receive their clinics and vaccines as resolved relations inside the page
 * story, so the merge has to reach into `content.body[].items[]` — the components downstream are
 * client components and cannot fetch.
 */
export const withVaccinationRelations = <T>(
  story: ISbStoryData<T>,
  {
    hoursByWebbId,
    campaignsByVaccineSlug
  }: {
    hoursByWebbId?: Record<string, VaccinationClinicOpeningHoursStoryblok>
    campaignsByVaccineSlug?: Record<string, VaccineCampaignStoryblok>
  }
): ISbStoryData<T> => {
  const body = (story.content as { body?: Blok[] } | undefined)?.body
  if (!body?.length) return story

  const mergedBody = body.map(blok => {
    const items = blok.items
    if (!Array.isArray(items)) return blok

    // An unresolved relation is a bare uuid string; leave those for the section's own handling.
    const mergedItems = items.map(item => {
      if (typeof item === "string") return item
      const entry = item as ISbStoryData<Record<string, unknown>>
      const content = entry.content

      if (hoursByWebbId && content?.component === "vaccination_clinic_type") {
        return {
          ...entry,
          content: mergeClinicHours(content as unknown as VaccinationClinicTypeStoryblok, hoursByWebbId)
        }
      }

      if (campaignsByVaccineSlug && content?.component === "vaccine_type") {
        // "vaccination/vaccin/tbe/config" -> "tbe"
        const vaccineSlug = entry.full_slug?.split("/").at(-2)
        return {
          ...entry,
          content: mergeVaccineCampaign(
            content as unknown as VaccineTypeStoryblok,
            vaccineSlug ? campaignsByVaccineSlug[vaccineSlug] : undefined
          )
        }
      }

      return item
    })

    return { ...blok, items: mergedItems }
  })

  return { ...story, content: { ...story.content, body: mergedBody } }
}
